import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./Login.css";
import "./Policy.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
// Cloudflare Turnstile site key is public by design (it ships in the page).
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "0x4AAAAAADqR-IaVCJmhWhDf";
const TURNSTILE_SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "12px 14px",
  marginTop: "6px",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: "8px",
  color: "#eef6ff",
  fontSize: "14px",
  fontFamily: "inherit",
};

const labelStyle = {
  display: "block",
  marginTop: "16px",
  fontSize: "13px",
  color: "rgba(210,230,255,0.75)",
  fontWeight: 600,
};

const errorTextStyle = {
  display: "block",
  marginTop: "6px",
  fontSize: "12px",
  fontWeight: 400,
  color: "#ff9a9a",
};

// Format a phone number as the user types. Domestic numbers become
// (555) 123-4567; a leading "+" switches to international (just "+" and digits).
function formatPhone(value) {
  if (value.trim().startsWith("+")) {
    return "+" + value.replace(/[^\d]/g, "").slice(0, 15);
  }
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export default function RequestAccountPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [form, setForm] = useState({ name: "", email: "", dealership: "", phone: "", message: "", company: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");

  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Load the Turnstile script (once) and render the widget explicitly.
  useEffect(() => {
    let cancelled = false;

    const renderWidget = () => {
      if (cancelled || !window.turnstile || !turnstileRef.current || widgetIdRef.current !== null) return;
      widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: "dark",
        callback: (token) => setCaptchaToken(token),
        "error-callback": () => setCaptchaToken(""),
        "expired-callback": () => setCaptchaToken(""),
      });
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      let script = document.querySelector(`script[src="${TURNSTILE_SCRIPT}"]`);
      if (!script) {
        script = document.createElement("script");
        script.src = TURNSTILE_SCRIPT;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      script.addEventListener("load", renderWidget);
      const poll = setInterval(() => {
        if (window.turnstile) {
          clearInterval(poll);
          renderWidget();
        }
      }, 300);
      setTimeout(() => clearInterval(poll), 10000);
    }

    return () => {
      cancelled = true;
    };
  }, []);

  const resetCaptcha = () => {
    setCaptchaToken("");
    if (window.turnstile && widgetIdRef.current !== null) {
      window.turnstile.reset(widgetIdRef.current);
    }
  };

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  // Field-format validation.
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const phoneDigits = form.phone.replace(/\D/g, "");
  const phoneValid =
    /^[0-9+()\-.\s]+$/.test(form.phone.trim()) && phoneDigits.length >= 10 && phoneDigits.length <= 15;

  // All fields required (and valid) except the trailing optional message.
  const formComplete = form.name.trim() && emailValid && form.dealership.trim() && phoneValid;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch(`${API_URL}/api/request-account`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, turnstileToken: captchaToken }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErrorMsg(data.error || "Something went wrong. Please try again.");
        setStatus("error");
        resetCaptcha(); // Turnstile tokens are single-use
        return;
      }
      setStatus("sent");
      setForm({ name: "", email: "", dealership: "", phone: "", message: "", company: "" });
      resetCaptcha();
    } catch (err) {
      setErrorMsg("Couldn't reach the server. Please try again later.");
      setStatus("error");
      resetCaptcha();
    }
  };

  return (
    <div className="policy-page">
      <Link to="/login" className="policy-back">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M19 12H5M12 5l-7 7 7 7" />
        </svg>
        Back to Login
      </Link>

      <div className="centered-logo-container">
        <div className="logo-wrapper">
          <img src="/MP%20Full%20Color@3x.png" alt="Masters Path" className="site-logo-img" />
        </div>
      </div>

      <div className="policy-card">
        <h1>Request an Account</h1>
        <p style={{ color: "rgba(210,230,255,0.7)", lineHeight: 1.6, marginTop: "8px" }}>
          Interested in the Masters Path Performance Dashboard for your dealership? Tell us a little about
          yourself and we'll be in touch to get you set up.
        </p>

        <hr className="policy-divider" />

        {status === "sent" ? (
          <div
            role="status"
            style={{
              padding: "20px",
              borderRadius: "10px",
              background: "rgba(46, 204, 113, 0.1)",
              border: "1px solid rgba(46, 204, 113, 0.35)",
              color: "#bdebcf",
            }}
          >
            <strong style={{ color: "#eafff2" }}>Thanks—your request has been sent.</strong>
            <br />
            We'll reach out to the email address you provided.
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            {/* Honeypot: hidden from users, bots tend to fill it */}
            <input
              type="text"
              name="company"
              value={form.company}
              onChange={update("company")}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
            />

            <label style={labelStyle}>
              Name
              <input type="text" required value={form.name} onChange={update("name")} style={inputStyle} maxLength={200} />
            </label>

            <label style={labelStyle}>
              Email
              <input type="email" required value={form.email} onChange={update("email")} style={inputStyle} maxLength={200} />
              {form.email.trim() && !emailValid && (
                <span style={errorTextStyle}>Please enter a valid email address.</span>
              )}
            </label>

            <label style={labelStyle}>
              Dealership
              <input type="text" required value={form.dealership} onChange={update("dealership")} style={inputStyle} maxLength={300} />
            </label>

            <label style={labelStyle}>
              Phone
              <input
                type="tel"
                required
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: formatPhone(e.target.value) }))}
                style={inputStyle}
                maxLength={18}
              />
              {form.phone.trim() && !phoneValid && (
                <span style={errorTextStyle}>Please enter a valid phone number (10–15 digits).</span>
              )}
            </label>

            <label style={labelStyle}>
              Anything else we should know? <span style={{ color: "rgba(210,230,255,0.4)", fontWeight: 400 }}>(optional)</span>
              <textarea value={form.message} onChange={update("message")} style={{ ...inputStyle, minHeight: "120px", resize: "vertical" }} maxLength={5000} />
            </label>

            <div ref={turnstileRef} style={{ marginTop: "18px" }} />

            {status === "error" && (
              <div className="login-error" style={{ marginTop: "14px" }}>{errorMsg}</div>
            )}

            <button
              type="submit"
              disabled={status === "sending" || !captchaToken || !formComplete}
              style={{
                marginTop: "22px",
                width: "100%",
                padding: "13px",
                background: "linear-gradient(90deg,#ff7a00,#ff4a00)",
                border: "none",
                borderRadius: "10px",
                color: "#fff",
                fontSize: "15px",
                fontWeight: 700,
                cursor: status === "sending" ? "default" : "pointer",
                opacity: status === "sending" ? 0.7 : 1,
              }}
            >
              {status === "sending" ? "Sending…" : "Submit request"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
